import { useMemo, useState } from "react";
import {
  Archive,
  Database,
  FileText,
  FileVideo,
  FolderTree,
  HardDrive,
  Image,
  Layers,
  RefreshCw,
  Search,
  Server,
  Video,
} from "lucide-react";
import { TkticSelect } from "@/components/ui/TkticSelect";
import { cn } from "@/lib/cn";
import type {
  AdminFileInventoryItem,
  AdminFileKind,
  AdminFileInventoryRoot,
} from "@/lib/types";
import { AdminIconButton } from "../components/AdminIconButton";
import { AdminPageHeader } from "../components/AdminPageHeader";
import { useAdminFiles } from "../hooks/useAdminData";

const KIND_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "all", label: "All files" },
  { value: "raw_video", label: "Raw uploads" },
  { value: "mp4", label: "Finished MP4" },
  { value: "hls_manifest", label: "HLS manifests" },
  { value: "hls_segment", label: "HLS segments" },
  { value: "thumbnail", label: "Thumbnails" },
  { value: "logo", label: "Logos" },
  { value: "asset", label: "Assets" },
  { value: "other", label: "Other" },
];

const ROOT_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "all", label: "All roots" },
  { value: "hot", label: "Hot media" },
  { value: "cold", label: "Original uploads" },
  { value: "assets", label: "App assets" },
];

const KIND_LABEL: Record<AdminFileKind, string> = {
  raw_video: "Raw upload",
  mp4: "Finished MP4",
  hls_manifest: "HLS manifest",
  hls_segment: "HLS segment",
  thumbnail: "Thumbnail",
  logo: "Logo",
  asset: "Asset",
  other: "Other",
};

const KIND_TONE: Record<AdminFileKind, string> = {
  raw_video: "border-accent/40 bg-accent/10 text-accent",
  mp4: "border-good/40 bg-good/10 text-good",
  hls_manifest: "border-rule bg-elevated text-ink",
  hls_segment: "border-rule bg-field text-ink-muted",
  thumbnail: "border-accent/30 bg-accent/10 text-accent",
  logo: "border-good/30 bg-good/10 text-good",
  asset: "border-rule bg-elevated text-ink-muted",
  other: "border-rule bg-elevated text-ink-muted",
};

export function AdminFilesPage() {
  const query = useAdminFiles();
  const [search, setSearch] = useState("");
  const [kind, setKind] = useState("all");
  const [root, setRoot] = useState("all");
  const payload = query.data;

  const rootOptions = useMemo(() => {
    const roots = new Set((payload?.roots ?? []).map((r) => r.id));
    return ROOT_OPTIONS.filter((opt) => opt.value === "all" || roots.has(opt.value as AdminFileInventoryRoot["id"]));
  }, [payload?.roots]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (payload?.files ?? []).filter((file) => {
      if (kind !== "all" && file.kind !== kind) return false;
      if (root !== "all" && file.root !== root) return false;
      if (!term) return true;
      const haystack = [
        file.relative_path,
        file.match_id,
        file.match_label,
        file.team_id,
        file.slot,
        file.root_label,
        KIND_LABEL[file.kind],
      ].join(" ").toLowerCase();
      return haystack.includes(term);
    });
  }, [kind, payload?.files, root, search]);

  const kindCounts = payload?.totals.by_kind ?? {};
  const roots = payload?.roots ?? [];

  return (
    <div data-testid="admin-files" className="flex flex-col gap-4">
      <AdminPageHeader
        eyebrow="Operations"
        title="Files"
        description="Visualize hot HLS output, raw uploads, thumbnails, logos, app assets, and media storage roots."
        meta={
          query.isFetching
            ? "Refreshing..."
            : `Updated ${formatGeneratedAt(payload?.generated_at)}`
        }
        actions={
          <AdminIconButton
            icon={RefreshCw}
            label="Refresh files"
            size="md"
            onClick={() => void query.refetch()}
            disabled={query.isFetching}
          />
        }
      />

      {query.error ? (
        <p data-testid="admin-files-error" className="text-xs font-mono text-destructive">
          Failed to load file inventory.
        </p>
      ) : null}

      <section className="grid gap-3 md:grid-cols-3">
        {roots.map((storageRoot) => (
          <StorageRootTile key={storageRoot.id} root={storageRoot} />
        ))}
      </section>

      <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <MetricTile
          icon={Archive}
          label="Raw uploads"
          value={String(kindCounts.raw_video ?? 0)}
          sub={formatBytes(bytesForKind(payload?.files ?? [], "raw_video"))}
        />
        <MetricTile
          icon={Video}
          label="Playable video"
          value={String((kindCounts.mp4 ?? 0) + (kindCounts.hls_manifest ?? 0))}
          sub={`${kindCounts.hls_segment ?? 0} HLS segments`}
        />
        <MetricTile
          icon={Image}
          label="Images"
          value={String((kindCounts.thumbnail ?? 0) + (kindCounts.logo ?? 0))}
          sub="Thumbnails, logos, favicons"
        />
        <MetricTile
          icon={Database}
          label="Scanned"
          value={payload ? String(payload.totals.file_count) : "0"}
          sub={payload?.truncated ? "Scan limit reached" : formatBytes(payload?.totals.bytes ?? 0)}
          tone={payload?.truncated ? "flag" : "neutral"}
        />
      </section>

      <section className="rounded border border-rule bg-surface shadow-sm">
        <div className="grid gap-2 border-b border-rule p-3 lg:grid-cols-[minmax(18rem,1fr)_12rem_12rem]">
          <label className="relative block">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted"
              size={16}
              aria-hidden="true"
            />
            <span className="sr-only">Search files</span>
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search path, match, team, slot..."
              className="h-9 w-full rounded border border-rule bg-field pl-9 pr-3 text-sm text-ink placeholder:text-ink-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            />
          </label>
          <TkticSelect
            value={kind}
            onChange={setKind}
            options={KIND_OPTIONS}
            ariaLabel="Filter by file type"
            size="sm"
          />
          <TkticSelect
            value={root}
            onChange={setRoot}
            options={rootOptions}
            ariaLabel="Filter by storage root"
            size="sm"
          />
        </div>

        <div className="hidden overflow-x-auto md:block">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-rule text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
              <tr>
                <th className="px-3 py-2 font-medium">Type</th>
                <th className="px-3 py-2 font-medium">File</th>
                <th className="px-3 py-2 font-medium">Match</th>
                <th className="px-3 py-2 font-medium">Size</th>
                <th className="px-3 py-2 font-medium">Modified</th>
                <th className="px-3 py-2 font-medium">Root</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-rule">
              {filtered.map((file) => (
                <FileTableRow key={file.id} file={file} />
              ))}
            </tbody>
          </table>
        </div>

        <div className="grid gap-2 p-3 md:hidden">
          {filtered.map((file) => (
            <FileMobileRow key={file.id} file={file} />
          ))}
        </div>

        {filtered.length === 0 ? (
          <p className="p-4 text-sm text-ink-muted">
            No files match the current filters.
          </p>
        ) : null}
      </section>
    </div>
  );
}

function StorageRootTile({ root }: { root: AdminFileInventoryRoot }) {
  const Icon = root.id === "assets" ? FolderTree : root.id === "cold" ? HardDrive : Server;
  return (
    <article className="rounded border border-rule bg-surface p-3 shadow-sm">
      <div className="flex items-start gap-3">
        <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded bg-elevated text-ink">
          <Icon size={18} aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-sm font-display uppercase tracking-wider text-ink">
              {root.label}
            </h3>
            <span className={cn(
              "rounded-full border px-2 py-0.5 text-[0.6rem] font-display uppercase tracking-wider",
              root.exists
                ? "border-good/40 bg-good/10 text-good"
                : "border-destructive/40 bg-destructive/10 text-destructive",
            )}>
              {root.exists ? "Online" : "Missing"}
            </span>
          </div>
          <p className="mt-1 break-all font-mono text-[0.65rem] text-ink-muted">
            {root.path}
          </p>
          <p className="mt-2 text-xs font-mono text-ink-muted">
            {root.file_count} files · {formatBytes(root.bytes)}
          </p>
        </div>
      </div>
    </article>
  );
}

function MetricTile({
  icon: Icon,
  label,
  value,
  sub,
  tone = "neutral",
}: {
  icon: typeof Database;
  label: string;
  value: string;
  sub: string;
  tone?: "neutral" | "flag";
}) {
  return (
    <article className="rounded border border-rule bg-surface p-3 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
            {label}
          </p>
          <p className={cn("mt-1 font-mono text-2xl", tone === "flag" ? "text-accent" : "text-ink")}>
            {value}
          </p>
        </div>
        <span className="inline-flex h-9 w-9 items-center justify-center rounded bg-elevated text-ink-muted">
          <Icon size={18} aria-hidden="true" />
        </span>
      </div>
      <p className="mt-2 text-xs font-mono text-ink-muted">{sub}</p>
    </article>
  );
}

function FileTableRow({ file }: { file: AdminFileInventoryItem }) {
  return (
    <tr className="align-top hover:bg-field/70">
      <td className="px-3 py-2">
        <KindPill kind={file.kind} />
      </td>
      <td className="max-w-[28rem] px-3 py-2">
        <div className="flex min-w-0 items-start gap-2">
          <FileKindIcon kind={file.kind} />
          <div className="min-w-0">
            <p className="break-all font-mono text-xs text-ink">{file.relative_path}</p>
            {file.slot ? (
              <p className="mt-1 text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
                Slot {file.slot}
              </p>
            ) : null}
          </div>
        </div>
      </td>
      <td className="px-3 py-2 text-xs text-ink-muted">
        {file.match_label || file.match_id ? (
          <div>
            <p className="max-w-[16rem] truncate text-ink">{file.match_label ?? file.match_id}</p>
            <p className="font-mono text-[0.65rem]">
              {file.match_id}
              {file.team_id ? ` · ${file.team_id}` : ""}
            </p>
          </div>
        ) : (
          <span>—</span>
        )}
      </td>
      <td className="px-3 py-2 font-mono text-xs text-ink-muted">{formatBytes(file.bytes)}</td>
      <td className="px-3 py-2 font-mono text-xs text-ink-muted">{formatDateTime(file.modified_at)}</td>
      <td className="px-3 py-2 text-xs text-ink-muted">{file.root_label}</td>
    </tr>
  );
}

function FileMobileRow({ file }: { file: AdminFileInventoryItem }) {
  return (
    <article className="rounded border border-rule bg-elevated p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <KindPill kind={file.kind} />
          <p className="mt-2 break-all font-mono text-xs text-ink">{file.relative_path}</p>
        </div>
        <FileKindIcon kind={file.kind} />
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-ink-muted">
        <p className="font-mono">{formatBytes(file.bytes)}</p>
        <p className="font-mono text-right">{formatDateTime(file.modified_at)}</p>
        <p>{file.root_label}</p>
        <p className="truncate text-right">{file.match_label ?? file.match_id ?? "Unlinked"}</p>
      </div>
    </article>
  );
}

function KindPill({ kind }: { kind: AdminFileKind }) {
  return (
    <span className={cn(
      "inline-flex rounded-full border px-2 py-0.5 text-[0.6rem] font-display uppercase tracking-wider",
      KIND_TONE[kind],
    )}>
      {KIND_LABEL[kind]}
    </span>
  );
}

function FileKindIcon({ kind }: { kind: AdminFileKind }) {
  const Icon =
    kind === "raw_video" || kind === "mp4"
      ? FileVideo
      : kind === "hls_manifest" || kind === "hls_segment"
        ? Layers
        : kind === "thumbnail" || kind === "logo"
          ? Image
          : FileText;
  return (
    <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded bg-elevated text-ink-muted">
      <Icon size={14} aria-hidden="true" />
    </span>
  );
}

function bytesForKind(files: AdminFileInventoryItem[], kind: AdminFileKind): number {
  return files.reduce((sum, file) => sum + (file.kind === kind ? file.bytes : 0), 0);
}

function formatBytes(bytes: number | undefined | null): string {
  if (!bytes || !Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let value = bytes;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  return `${value.toFixed(value < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}

function formatDateTime(value: string | undefined): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatGeneratedAt(value: string | undefined): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}
